import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Car, Cog, Factory, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DynoGraph } from "@/components/DynoGraph";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { icon: Factory, label: "Manufacturer" },
  { icon: Car, label: "Model" },
  { icon: Cog, label: "Engine" },
];

const selectTriggerCls =
  "w-full h-14 bg-panel border-[#262933] hover:border-nitro/60 focus:ring-nitro text-neutral-100 font-heading text-lg tracking-wide transition-colors duration-300 disabled:opacity-40";
const selectContentCls = "bg-panel border-[#262933] text-neutral-100";

export const VehicleSelector = ({ onBook }) => {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [engines, setEngines] = useState([]);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [engine, setEngine] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/vehicles/manufacturers`)
      .then((r) => setMakes(r.data.manufacturers))
      .catch(() => {});
  }, []);

  const pickMake = (v) => {
    if (v === "__not_listed__") {
      setMake(v);
      setModel("");
      setEngine(null);
      setEngines([]);
      setModels([]);
      return;
    }
    setMake(v);
    setModel("");
    setEngine(null);
    setEngines([]);
    axios.get(`${API}/vehicles/models`, { params: { make: v } }).then((r) => setModels(r.data.models));
  };

  const pickModel = (v) => {
    setModel(v);
    setEngine(null);
    axios
      .get(`${API}/vehicles/engines`, { params: { make, model: v } })
      .then((r) => setEngines(r.data.engines));
  };

  const pickEngine = (name) => {
    setEngine(engines.find((e) => e.name === name) || null);
  };

  const vehicleSummary = engine
    ? `${make} ${model} — ${engine.name} (Stage 1: ${engine.tuned_bhp} BHP / ${engine.tuned_nm} Nm)`
    : null;

  return (
    <section id="vehicle-selector" className="relative py-24 sm:py-32 carbon-grid">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-nitro mb-4 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-nitro" />
              Performance Calculator
            </p>
            <h2 className="font-heading font-bold uppercase text-4xl sm:text-5xl leading-[0.95] text-neutral-100">
              What is your car
              <span className="block text-outline-red">really capable of?</span>
            </h2>
            <p className="mt-5 text-neutral-400 text-base leading-relaxed max-w-md">
              Three steps. Pick your manufacturer, model and engine — we will show
              you the projected Stage 1 power and torque gains instantly.
            </p>
            <div className="mt-8 flex flex-col gap-4">
              {STEPS.map((s, i) => {
                const active = i === 0 ? !!make : i === 1 ? !!model : !!engine;
                return (
                  <div key={s.label} className="flex items-center gap-4">
                    <span
                      className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-500 ${
                        active
                          ? "border-nitro text-nitro shadow-[0_0_15px_rgba(255,46,77,0.4)]"
                          : "border-[#262933] text-neutral-500"
                      }`}
                    >
                      <s.icon size={17} />
                    </span>
                    <span
                      className={`font-mono text-[11px] tracking-[0.25em] uppercase transition-colors duration-500 ${
                        active ? "text-neutral-100" : "text-neutral-500"
                      }`}
                    >
                      0{i + 1} — {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            className="glass-panel rounded-2xl p-6 sm:p-9"
          >
            <div className="flex flex-col gap-5">
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500 block mb-2">
                  01 / Manufacturer
                </label>
                <Select value={make} onValueChange={pickMake}>
                  <SelectTrigger data-testid="vehicle-manufacturer-select" className={selectTriggerCls}>
                    <SelectValue placeholder="Select manufacturer" />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    {makes.map((m) => (
                      <SelectItem key={m} value={m} className="font-heading text-lg focus:bg-nitro/20 focus:text-white">
                        {m}
                      </SelectItem>
                    ))}
                    <div className="my-1 border-t border-[#262933]" />
                    <SelectItem
                      value="__not_listed__"
                      data-testid="vehicle-not-listed-option"
                      className="font-heading text-lg text-nitro focus:bg-nitro/20 focus:text-white"
                    >
                      My vehicle is not listed
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p data-testid="not-listed-note" className="mt-2 font-mono text-[10px] tracking-wide text-neutral-500 leading-relaxed">
                  Vehicle not in the list? Choose{" "}
                  <span className="text-nitro">“My vehicle is not listed”</span> above and we’ll
                  confirm compatibility when we call you.
                </p>
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500 block mb-2">
                  02 / Model
                </label>
                <Select value={model} onValueChange={pickModel} disabled={!make}>
                  <SelectTrigger data-testid="vehicle-model-select" className={selectTriggerCls}>
                    <SelectValue placeholder={make ? "Select model" : "Pick a manufacturer first"} />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    {models.map((m) => (
                      <SelectItem key={m} value={m} className="font-heading text-lg focus:bg-nitro/20 focus:text-white">
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500 block mb-2">
                  03 / Engine
                </label>
                <Select value={engine?.name || ""} onValueChange={pickEngine} disabled={!model}>
                  <SelectTrigger data-testid="vehicle-engine-select" className={selectTriggerCls}>
                    <SelectValue placeholder={model ? "Select engine" : "Pick a model first"} />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    {engines.map((e) => (
                      <SelectItem key={e.name} value={e.name} className="font-heading text-lg focus:bg-nitro/20 focus:text-white">
                        {e.name} · {e.stock_bhp} BHP
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <AnimatePresence>
              {make === "__not_listed__" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-7 rounded-lg border border-amber-400/40 bg-amber-400/10 px-5 py-4">
                    <p data-testid="not-listed-info" className="text-sm text-neutral-200 leading-relaxed">
                      No problem — book in below and we’ll confirm your vehicle’s compatibility
                      and projected gains when we call you.
                    </p>
                    <Button
                      data-testid="notlisted-book-cta"
                      onClick={() => onBook("Vehicle not listed — customer to confirm")}
                      className="mt-4 w-full bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-widest text-lg rounded-full py-6 animate-pulse-glow transition-transform duration-300 hover:scale-[1.02]"
                    >
                      Book Appointment
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {engine && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <Button
                    data-testid="selector-book-cta"
                    onClick={() => onBook(vehicleSummary)}
                    className="mt-7 w-full bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-widest text-lg rounded-full py-7 animate-pulse-glow transition-transform duration-300 hover:scale-[1.02]"
                  >
                    Book This Remap
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        <AnimatePresence mode="wait">
          {engine && <DynoGraph key={engine.name} engine={engine} />}
        </AnimatePresence>
      </div>
    </section>
  );
};
